/**
 * @author Mevan Nirosh
 * @version 0.1
 * @since 4/22/2024
 */
import React, { useState, useMemo, useEffect } from "react"
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Fab,
  Stack,
} from "@mui/material"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { DemoContainer } from "@mui/x-date-pickers/internals/demo"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import api from "../../../../../base/services/ResApi"
import { FetchErrorHandeling } from "../../../../../base/errorHandeling/FetchErrorHandeling"
import dayjs from "dayjs"
import FormControl from "@mui/material/FormControl"
import TroubleshootIcon from "@mui/icons-material/Troubleshoot"

const TerminalHealthHistory = ({ open, onClose, grpData, serialNo }) => {
  const [rowCount, setRowCount] = useState(0)
  const [data, setData] = useState([])
  const [dataLoading, setDataLoading] = useState(true)

  const [fromDate, setFromDate] = useState(dayjs().format("YYYY/MM/DD"))
  const [toDate, setToDate] = useState(dayjs().format("YYYY/MM/DD"))
  const [isSearch, setIsSearch] = useState(false)

  //for error handling
  const [iserror, setIserror] = useState(false)
  const [iserrorShow, setIserrorShow] = useState(false)
  const [isUnauhtError, setIsUnauhtError] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const columns = useMemo(
    () => [
      {
        accessorKey: "deviceStatus",
        header: "Status",
      },
      {
        accessorKey: "lockUnlockStatus",
        header: "Lock/Unlock",
      },
      {
        accessorKey: "batteryPct",
        header: "Battery %",
      },
      {
        accessorKey: "powerOnOffTimes",
        header: "On/Off times",
      },
      {
        accessorKey: "chargeTimes",
        header: "Charge times",
      },
      {
        accessorKey: "usbPlugTimes",
        header: "USB Plug times",
      },
      {
        accessorKey: "screenClickTimes",
        header: "Screen Clicks",
      },
      {
        accessorKey: "availMemPct",
        header: "Avail. Mem. %",
      },
      {
        accessorKey: "availStorPct",
        header: "Avail. Str. %",
      },
      {
        accessorKey: "lastPowerOn",
        header: "Last Power On",
      },

      {
        accessorKey: "totRunningTime",
        header: "Running Time",
      },
      {
        accessorKey: "hlthReceivedTime",
        header: "Health Received Time",
      },
    ],
    []
  )

  //Approve Data fetch
  useEffect(() => {
    if (isSearch || pagination.pageIndex || pagination.pageSize) {
      setDataLoading(true)
      let config = {
        headers: {
          Action: "VIEW_TMNL_HLTH_DATA",
          "Page-Number": pagination.pageIndex + 1,
          "Page-Size": pagination.pageSize,
          "Sort-Column": "tmnl_hlth_data_id",
          "Sort-Order": "desc",
          SN: serialNo,
          FDT: fromDate,
          TDT: toDate,
        },
      }

      api
        .get("/terminals/health", config)
        .then((res) => {
          setIsSearch(false)
          const tmnlHealthDataArray = []
          res.data?.dataList.forEach((item) => {
            // Access the tmnlHealthData field and push it to the array
            tmnlHealthDataArray.push({
              ...item.tmnlHealthData,
              hlthReceivedTime:item.hlthReceivedTime,
              availMemMb: item.tmnlHealthData.memory.availMemMb,
              availMemGb: item.tmnlHealthData.memory.availMemGb,
              availMemPct: item.tmnlHealthData.memory.availMemPct,
              totMemGb: item.tmnlHealthData.memory.totMemGb,
              availStorMb: item.tmnlHealthData.storage.availStorMb,
              availStorGb: item.tmnlHealthData.storage.availStorGb,
              availStorPct: item.tmnlHealthData.storage.availStorPct,
              totStorGb: item.tmnlHealthData.storage.totStorGb,
            })
          })
          setData(tmnlHealthDataArray)
          setRowCount(res.data?.total)
          setDataLoading(false)
          setErrorMessages([])
          setIserror(false)
          setIsUnauhtError(false)
        })
        .catch((error) => {
          setIsSearch(false)
          console.log(error)
          setDataLoading(false)
          FetchErrorHandeling(
            error,
            setIserror,
            setErrorMessages,
            setIserrorShow,
            setIsUnauhtError
          )
        })
    }
  }, [isSearch, isUnauhtError, pagination.pageIndex, pagination.pageSize])

  const handleSearch = () => {
    setIsSearch(true)
  }

  const fromDateChange = (e) => {
    setFromDate(dayjs(e).format("YYYY/MM/DD"))
  }
  const toDateChange = (e) => {
    setToDate(dayjs(e).format("YYYY/MM/DD"))
  }

  return (
    <div>
      <DialogContent dividers>
        <Stack direction="row" spacing={3}>
          <FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]}>
                <DatePicker
                  value={dayjs(fromDate)}
                  onChange={fromDateChange}
                  label="From"
                />
                <DatePicker
                  value={dayjs(toDate)}
                  onChange={toDateChange}
                  label="To"
                />
              </DemoContainer>
            </LocalizationProvider>
          </FormControl>
          <div style={{ marginTop: 12 }}>
            <Fab onClick={handleSearch} variant="extended" color="primary">
              <TroubleshootIcon sx={{ mr: 1 }} />
              Search
            </Fab>
          </div>
        </Stack>
        <br />
        <br />
        <MaterialReactTable
          columns={columns}
          data={data ?? []}
          state={{
            pagination,
            showProgressBars: dataLoading,
          }}
          getRowId={(row) => row.phoneNumber}
          initialState={{
            showColumnFilters: false,
            columnVisibility: {
              appId: false,
              versionCode: false,
              createdBy: false,
            },
          }}
          manualPagination
          //   onColumnFiltersChange={setColumnFilters}
          //   onGlobalFilterChange={setGlobalFilter}
          onPaginationChange={setPagination}
          rowCount={rowCount}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10],
          }}
          enableColumnOrdering
          enableGrouping
          enablePinning
          enableColumnResizing
          columnResizeMode="onEnd"
        />
        <br />
        <br />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: "1.25rem" }}>
        <Button onClick={onClose} variant="outlined" color="error">
          Cancel
        </Button>
      </DialogActions>
    </div>
  )
}

export default TerminalHealthHistory
